import React, { useEffect, useState, Fragment, useCallback, useMemo, lazy, Suspense } from 'react';
import { useCourseCollection } from '../hooks/useCourseCollection';
import { useCourseTrail } from '../hooks/useCourseTrail';
import { buildFolderDocPath, getFolderProgress } from '../hooks/folderProgress';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import { Card, CardBody, Divider, CardHeader, Spinner, Link, Tooltip, Breadcrumbs, BreadcrumbItem, Accordion, AccordionItem, Spacer } from '@nextui-org/react';
import { Button } from '@nextui-org/button';
import { motion, AnimatePresence } from 'framer-motion';
import { ReactComponent as Copy } from '../images/copy.svg'
import { ReactComponent as DarkCopy } from '../images/darkCopy.svg'
import { ReactComponent as SoundCloud } from '../images/soundcloud.svg'
import { ReactComponent as Documents } from '../images/documents.svg'
import { ReactComponent as DarkDocument } from '../images/darkDocuments.svg'
import { ReactComponent as Tag } from '../images/tag.svg'
import { ReactComponent as DarkTag } from '../images/darkTag.svg'
import { useTheme } from 'next-themes';
import Lottie from 'lottie-react';
import WIP from '../lottie/wip.json';
import useFetchDocuments from '../hooks/useFetchDocuments';
import MyInput from './SearchInput';
import { SunIconForAccordion } from './SunIcon';
import { filterDocs } from '../utils';
import { FaChevronUp } from "react-icons/fa";
import TiltedCard from './TiltedCard';
import SpotlightCard from './SpotlightCard';
import { useBatchProgress } from '../hooks/useBatchProgress';
import { trackEvent } from '../services/analytics';

// Lazy load components that are not immediately needed
const YoutubeSection = lazy(() => import('./YoutubeSection'));

// SpotlightCardWithProgress - Batch Processing Approach
const SpotlightCardWithProgress: React.FC<{
    doc: any;
    collectionPath: string;
    children: (props: { initialProgress: any }) => React.ReactNode;
}> = ({ doc, collectionPath, children }) => {
    // Saves update the shared progress cache directly (see setCachedProgress),
    // so no refetch callback is needed here.
    const { getProgress } = useBatchProgress();

    // Get progress from batch data
    const progress = getProgress(doc.id, collectionPath);

    return (
        <SpotlightCard
            description={doc.description}
            isPlaylist={doc.isPlaylist}
            isCompleted={progress?.completionStatus || false}
            isFavorited={progress?.isFavorited || false}
        >
            {children({
                initialProgress: progress
            })}
        </SpotlightCard>
    );
};

// Types
interface PathItem {
    title: string;
    path: string;
}

interface Document {
    id: string;
    name: string;
    hasChildren: boolean;
    description?: string;
    order?: number;
    soundCloudLink?: string;
    youtubeLink?: string;
    docsLink?: string;
    summary?: string;
    verseNumbers?: string;
    isPlaylist?: boolean;
    parentName?: string;
    timeStamps?: boolean;
    tagsAndTimeStamps?: Array<{ tag: string; timeStamp: string }>;
    // Folders only: completable lectures beneath, set nightly by a Cloud Function
    completableLectureCount?: number;
}

interface SearchResult {
    parentName: string;
    items: Document[];
    hasChildren: boolean;
}

// Expands/collapses in height so the cards below slide instead of jumping.
// Overflow is only hidden while animating so the accordion shadow isn't clipped.
const Collapsible: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isAnimating, setIsAnimating] = useState(true);

    return (
        <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            style={{ overflow: isAnimating ? 'hidden' : 'visible' }}
            onAnimationStart={() => setIsAnimating(true)}
            onAnimationComplete={() => setIsAnimating(false)}
        >
            {children}
        </motion.div>
    );
};

// Card entrance timing - stagger is capped so long lists don't keep users waiting
const CARD_ENTRANCE_DURATION = 0.4;
const CARD_STAGGER_STEP = 0.08;
const CARD_STAGGER_CAP = 8;

const cardEntranceTransition = (index: number) => ({
    duration: CARD_ENTRANCE_DURATION,
    delay: Math.min(index, CARD_STAGGER_CAP) * CARD_STAGGER_STEP,
});

const accordionMotionProps = {
    variants: {
        enter: {
            y: 0,
            opacity: 1,
            height: "auto",
            transition: {
                height: {
                    type: "spring",
                    stiffness: 500,
                    damping: 30,
                    duration: 1,
                },
                opacity: {
                    easings: "ease",
                    duration: 1,
                },
            },
        },
        exit: {
            y: -10,
            opacity: 0,
            height: 0,
            transition: {
                height: {
                    easings: "ease",
                    duration: 0.25,
                },
                opacity: {
                    easings: "ease",
                    duration: 0.3,
                },
            },
        },
    },
} as const;

const itemClasses = {
    base: "py-0 w-full",
    title: "text-xl text-darkBlue font-bold dark:text-lightBlue",
    trigger: "text-red",
    indicator: "text-medium",
    content: "text-bold text-black dark:text-lightGrey",
    subtitle: "text-md",
};

// Helper Components
const LoadingSpinner = () => (
    <Spinner
        color='primary'
        labelColor='primary'
        className='absolute top-1/2 left-1/2'
        size='lg'
    />
);

const FullPageError: React.FC<{
    onRetry: () => void;
    message?: string
}> = ({ onRetry, message = "Unable to load data" }) => (
    <div className='min-h-screen flex items-center justify-center'>
        <Card className='w-full max-w-md'>
            <CardHeader className='flex gap-3 p-8 justify-center flex-col items-center'>
                <div className='text-6xl mb-4'>⚠️</div>
                <div className='flex flex-col text-center'>
                    <p className='text-2xl font-bold text-danger mb-2'>
                        {message}
                    </p>
                    <p className='text-default-600 mb-6'>
                        Something went wrong while loading the page.
                    </p>
                    <Button
                        color='primary'
                        onClick={onRetry}
                        size='lg'
                    >
                        Try Again
                    </Button>
                </div>
            </CardHeader>
        </Card>
    </div>
);

const NoResults = () => (
    <Card className='w-full border-2 border-lightOrange'>
        <CardBody className={'flex flex-col justify-center items-center'}>
            <Lottie
                animationData={WIP}
                loop={true}
                autoplay={true}
                style={{ width: 300, height: 300, margin: 0 }}
            />
            <p className='text-lg text-turquiose dark:text-lightGrey'>No results found</p>
        </CardBody>
    </Card>
);

// Memoized helper function
const removeBeforeLastSlash = (input: string) => {
    const lastIndex = input.lastIndexOf('/');
    return lastIndex === -1 ? input : input.slice(lastIndex + 1).trim();
};

// Memoized Components
const NoChildrenContent = ({ doc, theme, collectionPath, initialProgress }: {
    doc: Document;
    theme: string | undefined;
    collectionPath?: string;
    initialProgress?: {
        completionStatus: boolean;
        isFavorited: boolean;
        notes: any;
    };
}) => {
    return (
        <>
            {doc.soundCloudLink && (
                <>
                    {doc.timeStamps && <Divider/>}
                    {doc.timeStamps && <div className='mt-2'/>}
                    <div className='flex justify-evenly mb-2'>
                        <div className='ml-0 mr-auto inline-flex'>
                            <div className='mr-2.5 mt-0.5'>
                                <SoundCloud/>
                            </div>
                            <p>Audio</p>
                        </div>
                        <Tooltip content='Open link in new tab'>
                            <Link isBlock showAnchorIcon href={doc.soundCloudLink || '#'} color='primary' isExternal/>
                        </Tooltip>
                        <Tooltip content='Copy link'>
                            <div
                                className='py-1 px-1 cursor-pointer mt-0.5'
                                onClick={() => navigator.clipboard.writeText(doc.soundCloudLink || '')}
                            >
                                {theme === 'light' ? <Copy/> : <DarkCopy/>}
                            </div>
                        </Tooltip>
                    </div>
                </>
            )}

            {doc.youtubeLink && (
                <Suspense fallback={<Spinner/>}>
                    <YoutubeSection
                        doc={doc}
                        collectionPath={collectionPath}
                        initialProgress={initialProgress}
                    />
                </Suspense>
            )}

            {doc.docsLink && (
                <>
                    {doc.youtubeLink && <Divider/>}
                    <div className='flex justify-evenly mb-2 mt-2'>
                        <div className='ml-0 mr-auto inline-flex'>
                            <div className='mr-2.5 mt-0.5'>
                                {theme === 'light' ? <Documents/> : <DarkDocument/>}
                            </div>
                            Class Notes
                        </div>
                        <Tooltip content='Open link in new tab'>
                            <Link isBlock showAnchorIcon href={doc.docsLink || '#'} color='primary' isExternal/>
                        </Tooltip>
                        <Tooltip content='Copy link'>
                            <div
                                className='py-1 px-1 cursor-pointer mt-0.5'
                                onClick={() => navigator.clipboard.writeText(doc.docsLink || '')}
                            >
                                {theme === 'light' ? <Copy/> : <DarkCopy/>}
                            </div>
                        </Tooltip>
                    </div>
                </>
            )}

            {doc.summary && (
                <>
                    {(doc.youtubeLink || doc.docsLink) && <Divider/>}
                    <div className='flex justify-evenly mb-2 mt-2'>
                        <div className='ml-0 mr-auto inline-flex'>
                            <div className='mr-2.5 mt-0.5'>
                                {theme === 'light' ? <Tag/> : <DarkTag/>}
                            </div>
                            Summary Notes
                        </div>
                        <Tooltip content='Open link in new tab'>
                            <Link isBlock showAnchorIcon href={doc.summary || '#'} color='primary' isExternal/>
                        </Tooltip>
                        <Tooltip content='Copy link'>
                            <div
                                className='py-1 px-1 cursor-pointer mt-0.5'
                                onClick={() => navigator.clipboard.writeText(doc.summary || '')}
                            >
                                {theme === 'light' ? <Copy/> : <DarkCopy/>}
                            </div>
                        </Tooltip>
                    </div>
                </>
            )}
        </>
    );
};

export const addLineBreak = (str: string) => {
    return str?.split('\\n').map((subStr, index) => {
        return (
            <Fragment key={index}>
                {subStr}
                <br/>
            </Fragment>
        );
    });
}

const MainCourses = () => {
    const { theme } = useTheme();
    const { pathname } = useLocation();
    const showCourses = localStorage.getItem("showCourses");
    const navigate = useNavigate();

    const [filteredDocs, setFilteredDocs] = useState<SearchResult[] | null>(null);
    const [searchValue, setSearchValue] = useState<string>('');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [tag, setTag] = useState<string>('');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [toggle, setToggle] = useState<boolean>(true);
    const [onResetResult, setOnResetResult] = useState<boolean>(false);
    const [showScrollTop, setShowScrollTop] = useState<boolean>(false);

    const { documents: docs, loading, error: docsError } = useCourseCollection<Document>(pathname);
    // Search index (only with the showCourses flag). A failure disables search
    // but never blocks the course page.
    const { documents, error: searchIndexError } = useFetchDocuments();

    // Retry function for error handling
    const retryDataFetch = useCallback(() => {
        window.location.reload(); // Simple retry by reloading the page
    }, []);

    // Memoized values
    // const hasDocuments = useMemo(() => !!documents, [documents]);
    const isSearchDisabled = useMemo(() => !documents || searchIndexError, [documents, searchIndexError]);

    // Helper functions
    const setInputDescription = useCallback(() => {
        // Check the failure first: a failed download also leaves documents empty
        if (searchIndexError) {
            return "Search is currently disabled as indexing has failed. Refresh the page.";
        }
        if (!documents) {
            return "Indexing in progress. Please wait to begin your search.";
        }
        return "Type to search courses";
    }, [documents, searchIndexError]);

    // Memoized callbacks
    const handleSearchValueChange = useCallback((value: string) => {
        setSearchValue(value);
    }, []);

    const handleResetResults = useCallback(() => {
        setSearchValue('');
        setOnResetResult(true);
    }, []);

    // Memoized filter effect
    useEffect(() => {
        if (!documents) return;

        const value = searchValue || tag;
        if (!value) {
            setFilteredDocs(null);
            return;
        }

        const results = filterDocs(value, documents) as SearchResult[];
        setFilteredDocs(results);
    }, [searchValue, tag, documents]);

    // Analytics: record a search once the visitor stops typing
    useEffect(() => {
        const term = searchValue.trim();
        if (term.length < 2 || !filteredDocs) return;
        const timeoutId = setTimeout(() => {
            const resultsCount = filteredDocs.reduce((count, group) => count + group.items.length, 0);
            trackEvent('search', { search_term: term, results_count: resultsCount });
        }, 1500);
        return () => clearTimeout(timeoutId);
    }, [searchValue, filteredDocs]);

    // Scroll effect with debounce
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            window.scrollTo({
                top: 0,
                behavior: "smooth"
            });
        }, 250);

        return () => clearTimeout(timeoutId);
    }, [docs]);

    // Breadcrumbs and description are derived from the URL. When clicking through
    // they come from the cached parent list in the same render as the cards; on a
    // refresh or shared link the missing levels are read, then the header expands.
    const courseTrail = useCourseTrail(pathname);
    // Shared cached query (the same one lecture cards use), empty when logged out
    const { progressMap } = useBatchProgress();
    const pathArray: PathItem[] = courseTrail ?? [];
    const currentLevel = courseTrail?.[courseTrail.length - 1];
    const descriptionToBeShown = currentLevel?.path === pathname.replace(/\/+$/, '') ? currentLevel.description : '';

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 300) {
                setShowScrollTop(true);
            } else {
                setShowScrollTop(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    // Memoized JSX elements
    const searchSection = useMemo(() => (
        showCourses && (
            <>
                {!!searchValue && (
                    <Link
                        className='flex justify-start mb-1.5 cursor-pointer text-lg font-bold'
                        onClick={handleResetResults}
                    >
                        Reset results
                    </Link>
                )}
                <div className='overflow-hidden h-[80px] mb-5'>
                    {toggle && (
                        <AnimatePresence>
                            <motion.div
                                initial={{ translateY: "-100%", opacity: 0 }}
                                animate={{ translateY: "0%", opacity: 1 }}
                                exit={{ translateY: "-100%", opacity: 0 }}
                                style={{ overflow: "hidden" }}
                                transition={{ type: "spring", duration: 0.7 }}
                            >
                                <MyInput
                                    onChangeValue={handleSearchValueChange}
                                    shouldDisable={isSearchDisabled}
                                    searchDescription={setInputDescription()}
                                    onResetResult={onResetResult}
                                />
                            </motion.div>
                        </AnimatePresence>
                    )}
                </div>
            </>
        )
    ), [showCourses, searchValue, toggle, handleResetResults, handleSearchValueChange, isSearchDisabled, onResetResult, setInputDescription]);

    // Error handling - the course list itself failed to load
    if (docsError) {
        return <FullPageError onRetry={retryDataFetch}/>;
    }

    // Loading logic - SIMPLIFIED: only wait for documents
    const isPageLoading = loading; // REMOVED ALL PROGRESS LOADING
    if (isPageLoading) {
        return <LoadingSpinner/>;
    }


    const noChildrenJSX = (doc: any, customPath?: string) => {
        // For search results, extract the collection path from parentName
        // parentName format: "courses / category / children / subcategory / children"
        let collectionPath = customPath || pathname;

        if (doc.parentName && !customPath) {
            // Convert parentName to a proper collection path
            // Remove spaces around slashes and convert to proper path
            const pathParts = doc.parentName.split(' / ').filter((part: string) => part !== 'courses');
            if (pathParts.length > 0) {
                collectionPath = '/courses/' + pathParts.join('/');
            }
        }

        // TEMPORARILY USE INDIVIDUAL HOOKS FOR ALL CASES
        return (
            <motion.div>
                <p className={`text-left mb-4 text-darkBlue dark:text-lightGrey font-bold text-xl ml-2.5 ${doc.isPlaylist && 'text-lightOrange font-extrabold'}`}>{doc.name}</p>

                <SpotlightCardWithProgress doc={doc} collectionPath={collectionPath}>
                    {({ initialProgress }) => (
                        <NoChildrenContent
                            doc={doc}
                            theme={theme}
                            collectionPath={collectionPath}
                            initialProgress={initialProgress}
                        />
                    )}
                </SpotlightCardWithProgress>
            </motion.div>
        );
    }


    return (
        <main className='md:mx-24 md:my-10 m-8 pb-14'>
            {searchSection}

            <AnimatePresence>
                {pathArray.length > 1 && filteredDocs === null && <Collapsible key='breadcrumbs'>
                    <div className='flex flex-col flex-wrap gap-4 mb-8 sm:ml-1 ml-0.5 overflow-scroll scrollbar-hide'>
                        {
                            <Breadcrumbs color={`${theme === 'light' ? 'primary' : 'foreground'}`} size={'lg'} separator='/'
                                         itemClasses={{
                                             item: "text-darkBlue dark:text-lightGrey data-[current=true]:text-lightOrange dark:data-[current=true]:text-lightBlue text-xl",
                                             separator: "px-2"
                                         }}
                            >
                                {pathArray.map((individualPathDetails: any) => (
                                    <BreadcrumbItem onPress={
                                        () => navigate(individualPathDetails.path)} key={individualPathDetails.path} className={'font-bold text-xl'}>
                                        {individualPathDetails.title}
                                    </BreadcrumbItem>
                                ))}
                            </Breadcrumbs>
                        }
                    </div>
                </Collapsible>}
            </AnimatePresence>
            <AnimatePresence>
                {descriptionToBeShown &&
                    <Collapsible key='course-description'>
                        <Accordion variant={'splitted'} className={'relative pl-0 before:absolute before:bg-darkBlue   before:top-0 before:bottom-[28.5px] before:left-0 before:border-l-8 before:border-solid before:border-red before:rounded-l-3xl text-red before:opacity-90'}
                                   itemClasses={itemClasses}
                                   motionProps={{
                                       variants: {
                                           enter: {
                                               y: 0,
                                               opacity: 1,
                                               height: "auto",
                                               transition: {
                                                   height: {
                                                       type: "spring",
                                                       stiffness: 300,
                                                       damping: 30,
                                                       duration: 1,
                                                   },
                                                   opacity: {
                                                       easings: "ease",
                                                       duration: 1,
                                                   },
                                               },
                                           },
                                           exit: {
                                               y: -10,
                                               opacity: 0,
                                               height: 0,
                                               transition: {
                                                   height: {
                                                       easings: "ease",
                                                       duration: 0.25,
                                                   },
                                                   opacity: {
                                                       easings: "ease",
                                                       duration: 0.3,
                                                   },
                                               },
                                           },
                                       },
                                   }}>
                            <AccordionItem
                                key='2'
                                aria-label='About this course'
                                title='About this course'
                                className={'pl-0 mb-7'}
                                subtitle={
                                    <p className='text-black dark:text-lightGrey text-md'>
                                        Click to expand/collapse
                                    </p>
                                }
                            >
                                <AnimatePresence mode='wait' initial={false}>
                                    <motion.p
                                        key={pathname}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.1 }}
                                        className={'italic leading-7 text-black dark:text-lightGrey'}
                                    >
                                        {addLineBreak(descriptionToBeShown)}
                                    </motion.p>
                                </AnimatePresence>
                            </AccordionItem>
                        </Accordion>
                        <Divider className={'mb-10'}/>
                    </Collapsible>
                }
            </AnimatePresence>
            {docs && filteredDocs === null && !searchValue && (() => {
                const hasChildrenDocs = docs.filter(doc => doc.hasChildren);
                const noChildrenDocs = docs.filter(doc => !doc.hasChildren);

                // Pre-calculate image found status
                const imageFoundArray = hasChildrenDocs.map(doc => {
                    try {
                        return !!require(`../images/${doc.id}.jpg`);
                    } catch (e) {
                        return false;
                    }
                });

                const handleCardClick = (doc: any) => {
                    navigate(`${pathname}/${doc.id}/children`);
                };

                return (
                    <>
                        {hasChildrenDocs.length > 0 && (
                            <motion.div className='gap-10 grid xl:grid-cols-3 lg:grid-cols-2 2xl:grid-cols-4 gap-y-16'>
                                {hasChildrenDocs.map((doc, index) => (
                                    <motion.div
                                        key={doc.id}
                                        initial={{
                                            opacity: 0,
                                            translateX: index % 2 === 0 ? -50 : 50,
                                            translateY: -50,
                                        }}
                                        animate={{ opacity: 1, translateX: 0, translateY: 0 }}
                                        transition={cardEntranceTransition(index)}
                                        className={'justify-self-center'}
                                    >
                                        <TiltedCard
                                            imageSrc={imageFoundArray[index] ? require(`../images/${doc.id}.jpg`) : require(`../images/imageNotFound.jpeg`)}
                                            altText={doc.name}
                                            name={doc.name}
                                            order={doc.order || 0}
                                            onClick={() => handleCardClick(doc)}
                                            containerHeight='420px'
                                            containerWidth='360px'
                                            scaleOnHover={1.05}
                                            rotateAmplitude={10}
                                            showTooltip={false}
                                            progress={getFolderProgress(progressMap, buildFolderDocPath(pathname, doc.id), doc.completableLectureCount)}
                                        />
                                    </motion.div>
                                ))}
                            </motion.div>
                        )}

                        {noChildrenDocs.length > 0 && (
                            <motion.div className={`gap-10 grid lg:grid-cols-2 gap-y-16 xs:grid-cols-1 ${hasChildrenDocs.length > 0 ? 'mt-10' : ''}`}>
                                {noChildrenDocs.map((doc, index) => (
                                    <Fragment key={doc.id}>
                                        <motion.div
                                            initial={{
                                                opacity: 0,
                                                translateX: index % 2 === 0 ? -50 : 50,
                                                translateY: -50,
                                            }}
                                            animate={{ opacity: 1, translateX: 0, translateY: 0 }}
                                            transition={cardEntranceTransition(index)}
                                            className={`${doc.isPlaylist ? 'col-start-1 sm:col-end-3 col-end-2' : 'auto'} `}
                                        >
                                            {noChildrenJSX(doc)}
                                        </motion.div>
                                    </Fragment>
                                ))}
                            </motion.div>
                        )}
                    </>
                );
            })()}
            {filteredDocs && filteredDocs.length > 0 && (
                <>
                    <h2 className={'flex justify-center md:text-3xl text-darkBlue font-semibold text-xl mb-2.5 md:mb-0'}>
                        Search Results for "{searchValue}"
                    </h2>
                    <p className={'justify-center md:text-lg text-lightOrange mb-5 font-semibold text-md hidden md:flex'}>
                        Click each section to view the search results organized by course name.
                    </p>
                    <motion.div className=''>
                        <Accordion
                            variant='splitted'
                            motionProps={accordionMotionProps}
                        >
                            {filteredDocs.map((doc, index) => (
                                <AccordionItem
                                    key={index}
                                    title={
                                        <>
                                            <p className='text-darkBlue lg:text-lg font-bold hidden lg:block'>
                                                {doc.parentName}
                                            </p>
                                            <p className='text-darkBlue lg:text-lg font-bold block lg:hidden'>
                                                {removeBeforeLastSlash(doc.parentName)}
                                            </p>
                                        </>
                                    }
                                    className={'border-lightOrange/70 border-2'}
                                    indicator={<SunIconForAccordion/>}
                                >
                                    <motion.div className='gap-10 grid lg:grid-cols-2 gap-y-16 xs:grid-cols-1'>
                                        {doc.items.map((item: Document, docIndex: number) => (
                                            <Fragment key={docIndex}>
                                                {!item.hasChildren && (
                                                    <motion.div
                                                        initial={{
                                                            opacity: 0,
                                                            translateX: index % 2 === 0 ? -50 : 50,
                                                            translateY: -50,
                                                        }}
                                                        animate={{ opacity: 1, translateX: 0, translateY: 0 }}
                                                        transition={{ duration: 0.2, }}
                                                        className={`${item.isPlaylist ? 'col-start-1 sm:col-end-3 col-end-2' : 'auto'} `}
                                                    >
                                                        {noChildrenJSX(item)}
                                                        <Spacer y={4}/>
                                                    </motion.div>
                                                )}
                                            </Fragment>
                                        ))}
                                    </motion.div>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </motion.div>
                </>
            )}
            {docs?.length === 0 && !loading && (
                <>
                    <div className={'hidden md:flex justify-center mt-10'}>
                        <Lottie
                            animationData={WIP}
                            loop={true}
                            autoplay={true}
                            style={{ width: 300, height: 300, margin: 0 }}
                        />
                    </div>
                    <Card className='w-full'>
                        <CardHeader className='flex gap-3 p-5 justify-center'>
                            <div className='flex flex-col'>
                                <p className='text-xl text-center'>
                                    Live {' '}
                                    <RouterLink className='font-extrabold text-darkBlue dark:text-skyblue' to={'/sessions'}>
                                        Zoom Sessions
                                    </RouterLink> are happening now for this section. please join the class or come back later for recorded sessions.
                                </p>
                            </div>
                        </CardHeader>
                    </Card>
                </>
            )}
            {filteredDocs?.length === 0 && <NoResults/>}
            <AnimatePresence>
                {showScrollTop && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.2 }}
                        className='fixed bottom-8 right-8 z-50'
                    >
                        <Button
                            isIconOnly
                            className='bg-darkBlue text-white hover:opacity-80 transition-opacity duration-200'
                            size='lg'
                            onClick={scrollToTop}
                        >
                            <FaChevronUp className='w-4 h-4'/>
                        </Button>
                    </motion.div>
                )}
            </AnimatePresence>
        </main>
    );
};

export default React.memo(MainCourses);

